//@ts-check

/**
 * TACOS Module - Tracking and Analytics Code Orchestration System
 * 
 * This module implements the TACOS initialization sequence as defined in the PlantUML diagram:
 * - Data Collection Phase: Collects ITC, referrer, and login state
 * - Session Validation: Validates stored data consistency 
 * - API Update Flow: Updates tracking codes when data mismatch is detected
 * 
 * @author STRATO Development Team
 * @version 2.0.0
 */

/**
 * @typedef {Object} TacosAction
 * @property {string} action - The action type (ADD_TRACKINGCODE_COMP, ADD_ACTIONCODE, ADD_MEDIUMCODE, ADD_COOKIE)
 * @property {Object} actionAttributes - Attributes for the specific action
 */

/**
 * @typedef {Object} SalesChannelResponse
 * @property {TacosAction[]} actions - Array of tracking actions to execute
 */

/**
 * @typedef {Object} ApiParams
 * @property {string} path - Current page path
 * @property {string} origin - Origin identifier (STRATO)
 * @property {Object} requestParameter - Query parameters (itc)
 * @property {Array} cookies - Current cookies
 * @property {string} referer - Document referrer
 * @property {boolean} isExistingCustomer - Login state from localStorage
 * @property {string} apiUrl - API endpoint URL
 */

// =============================================================================
// DEBUG CONSOLE WRAPPER
// =============================================================================

/**
 * Console wrapper that only outputs when localStorage has debug = true
 */
const debugConsole = {
    /**
     * Checks if debug mode is enabled
     * @returns {boolean} True if debug mode is enabled
     */
    isDebugEnabled() {
        if (typeof localStorage === 'undefined') return false;
        try {
            return localStorage.getItem('debug') === 'true';
        } catch (error) {
            return false;
        }
    },

    /**
     * Debug console.log wrapper
     * @param {...any} args - Arguments to log
     */
    log(...args) {
        if (this.isDebugEnabled()) {
            console.log(...args);
        }
    },

    /**
     * Debug console.warn wrapper
     * @param {...any} args - Arguments to warn
     */
    warn(...args) {
        if (this.isDebugEnabled()) {
            console.warn(...args);
        }
    },

    /**
     * Debug console.error wrapper (always shows errors regardless of debug mode)
     * @param {...any} args - Arguments to error
     */
    error(...args) {
        console.error(...args);
    }
};

// =============================================================================
// BROWSER INTERFACE FUNCTIONS (Data Collection Phase)
// =============================================================================

/**
 * Gets query parameter value from current URL
 * @param {string} name - Parameter name to retrieve
 * @returns {string|null} Parameter value or null if not found
 */
export function getQueryParam(name) {
    if (typeof window === 'undefined' || !window.location?.search) {
        return null;
    }
    
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
}

/**
 * Gets current document referrer
 * @returns {string} Document referrer URL
 */
export function getDocumentReferrer() {
    return typeof document !== 'undefined' ? document.referrer : '';
}

// =============================================================================
// COOKIE MANAGER FUNCTIONS
// =============================================================================

/**
 * Gets ITC cookie value
 * @returns {string|null} Cookie value or null if not found
 */
export function getCookie(name = 'itc') {
    if (typeof document === 'undefined') return null;
    
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
        const [key, value] = cookie.trim().split('=');
        if (key === name) {
            return decodeURIComponent(value);
        }
    }
    return null;
}

/**
 * Sets ITC cookie with 180-day expiry
 * @param {string} value - Cookie value to set
 * @param {string} name - Cookie name (default: 'itc')
 */
export function setCookie(value, name = 'itc') {
    if (typeof document === 'undefined') return;
    
    const cookieExpiryDays = 180;
    const expiryDate = new Date();
    expiryDate.setTime(expiryDate.getTime() + (cookieExpiryDays * 24 * 60 * 60 * 1000));
    
    const cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expiryDate.toUTCString()}; path=/; secure;`;
    document.cookie = cookieString;
}

/**
 * Gets all cookies formatted for API request
 * @returns {Array<{name: string, value: string}>} Array of cookie objects
 */
// eslint-disable-next-line max-lines-per-function
export function getAllCookies() {
    if (typeof document === 'undefined') return [];
    
    const cookies = [];
    const cookieString = document.cookie;
    
    if (cookieString) {
        cookieString.split(';').forEach(cookie => {
            const trimmedCookie = cookie.trim();
            const equalIndex = trimmedCookie.indexOf('=');
            
            if (equalIndex > 0) {
                const name = trimmedCookie.substring(0, equalIndex).trim();
                const value = trimmedCookie.substring(equalIndex + 1).trim();
                
                if (name && value) {
                    cookies.push({
                        name: decodeURIComponent(name),
                        value: decodeURIComponent(value)
                    });
                }
            }
        });
    }
    
    return cookies;
}

// =============================================================================
// LOCAL STORAGE FUNCTIONS
// =============================================================================

/**
 * Gets login state from localStorage
 * @returns {boolean} True if user is logged in (login_state exists)
 */
export function getLoginState() {
    if (typeof localStorage === 'undefined') return false;
    
    try {
        return localStorage.getItem('login_state') !== null;
    } catch (error) {
        debugConsole.warn('Failed to access localStorage:', error);
        return false;
    }
}

// =============================================================================
// SESSION STORAGE FUNCTIONS (Session Validation)
// =============================================================================

/**
 * Checks if sessionStorage is available
 * @returns {boolean} True if sessionStorage is available
 */
export function isSessionStorageAvailable() {
    if (typeof sessionStorage === 'undefined') return false;
    
    try {
        const testKey = '__tacos_test__';
        sessionStorage.setItem(testKey, '1');
        sessionStorage.removeItem(testKey);
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Gets stored TACOS data from sessionStorage
 * @returns {Object} Object containing stored TACOS values
 */
// eslint-disable-next-line max-lines-per-function
export function getStoredTacosData() {
    if (!isSessionStorageAvailable()) {
        return {
            tacosItc: null,
            tacosReferrer: null,
            tacosIsExistingCustomer: null
        };
    }
    
    try {
        return {
            tacosItc: sessionStorage.getItem('tacosItc'),
            tacosReferrer: sessionStorage.getItem('tacosReferrer'),
            tacosIsExistingCustomer: sessionStorage.getItem('tacosIsExistingCustomer')
        };
    } catch (error) {
        debugConsole.warn('Failed to read from sessionStorage:', error);
        return {
            tacosItc: null,
            tacosReferrer: null,
            tacosIsExistingCustomer: null
        };
    }
}

/**
 * Stores TACOS data in sessionStorage
 * @param {string|null} itc - ITC value to store
 * @param {string} referrer - Referrer to store
 * @param {boolean} isExistingCustomer - Customer status to store
 */
// eslint-disable-next-line max-lines-per-function
export function setStoredTacosData(itc, referrer, isExistingCustomer) {
    if (!isSessionStorageAvailable()) return;
    
    try {
        if (itc) {
            sessionStorage.setItem('tacosItc', itc);
        }
        
        // Get current stored referrer for sticky logic
        const currentStoredReferrer = sessionStorage.getItem('tacosReferrer');
        
        // Store referrer host with sticky external referrer logic
        const referrerHost = extractReferrerHost(referrer, null, currentStoredReferrer);
        sessionStorage.setItem('tacosReferrer', referrerHost);
        sessionStorage.setItem('tacosIsExistingCustomer', String(isExistingCustomer));
        
        debugConsole.log('Session storage updated:', {
            tacosItc: itc,
            tacosReferrer: referrerHost,
            tacosIsExistingCustomer: String(isExistingCustomer)
        });
    } catch (error) {
        debugConsole.warn('Failed to write to sessionStorage:', error);
    }
}

// =============================================================================
// DATA CONSISTENCY CHECK
// =============================================================================

/**
 * Normalizes ITC value by removing trailing dash
 * @param {string|null} itc - ITC value to normalize
 * @returns {string|null} Normalized ITC value
 */
export function normalizeItc(itc) {
    if (typeof itc === 'string' && itc.endsWith('-')) {
        return itc.slice(0, -1);
    }
    return itc;
}

/**
 * Extracts referrer host from referrer URL with sticky external referrer logic
 * - External referrers are "sticky" - once set, internal navigation won't overwrite them
 * - Only new external referrers can overwrite existing external referrers
 * - Internal referrers are stored as current host only when no external referrer exists
 * @param {string} referrer - Full referrer URL
 * @param {string} currentHost - Current application host (optional, defaults to window.location.host)
 * @param {string} storedReferrer - Previously stored referrer host (optional, for sticky logic)
 * @returns {string} Referrer host following sticky external referrer logic
 */
/* eslint-disable max-lines-per-function, complexity */
export function extractReferrerHost(referrer, currentHost = null, storedReferrer = null) {
    if (!referrer || typeof referrer !== 'string') return storedReferrer || '';
    
    // Get current host if not provided
    if (!currentHost && typeof window !== 'undefined') {
        currentHost = window.location.host;
    }
    
    try {
        const referrerUrl = new URL(referrer);
        const referrerHost = referrerUrl.host;
        
        // Check if current referrer is internal (same host)
        const isInternalReferrer = currentHost && referrerHost === currentHost;
        
        if (isInternalReferrer) {
            // Internal referrer - check if we have a stored external referrer
            if (storedReferrer && storedReferrer !== currentHost && storedReferrer !== '') {
                // Keep existing external referrer (sticky behavior)
                debugConsole.log('Internal referrer detected, keeping external referrer:', storedReferrer);
                return storedReferrer;
            } else {
                // No external referrer stored, use current host
                debugConsole.log('Internal referrer detected, using current host:', currentHost);
                return currentHost;
            }
        } else {
            // External referrer - always update (new external referrer overwrites old one)
            debugConsole.log('External referrer detected:', referrerHost);
            return referrerHost;
        }
    } catch (e) {
        // Invalid URL, return stored referrer or empty string
        return storedReferrer || '';
    }
}

/**
 * Checks if all parameters match stored values (Data Consistency Check)
 * @param {string|null} currentItc - Current ITC value
 * @param {string} currentReferrer - Current referrer
 * @param {boolean} currentIsExistingCustomer - Current customer status
 * @param {Object} storedData - Stored TACOS data from sessionStorage
 * @returns {boolean} True if all parameters match, false if mismatch detected
 */
// eslint-disable-next-line max-lines-per-function
export function doParametersMatch(currentItc, currentReferrer, currentIsExistingCustomer, storedData) {
    // Normalize current values
    const normalizedCurrentItc = normalizeItc(currentItc);
    const currentReferrerHost = extractReferrerHost(currentReferrer, null, storedData.tacosReferrer);
    const currentCustomerStatus = String(currentIsExistingCustomer);
    
    // Check ITC match
    if (normalizedCurrentItc !== storedData.tacosItc) {
        debugConsole.log('ITC mismatch:', { current: normalizedCurrentItc, stored: storedData.tacosItc });
        return false;
    }
    
    // Check referrer match
    if (currentReferrerHost !== storedData.tacosReferrer) {
        debugConsole.log('Referrer mismatch:', { current: currentReferrerHost, stored: storedData.tacosReferrer });
        return false;
    }
    
    // Check customer status match
    if (currentCustomerStatus !== storedData.tacosIsExistingCustomer) {
        debugConsole.log('Customer status mismatch:', { current: currentCustomerStatus, stored: storedData.tacosIsExistingCustomer });
        return false;
    }
    
    return true;
}

// =============================================================================
// SALES CHANNEL API FUNCTIONS (API Update Flow)
// =============================================================================

/**
 * Builds API parameters for SalesChannel API request
 * @param {string|null} itc - Current ITC value
 * @param {string} referrer - Current referrer
 * @param {boolean} isExistingCustomer - Current customer status
 * @returns {ApiParams} API parameters object
 */
export function buildApiParams(itc, referrer, isExistingCustomer) {
    const requestParameter = {};
    if (itc) {
        requestParameter.itc = itc;
    }
    
    return {
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        origin: 'STRATO',
        requestParameter,
        cookies: getAllCookies(),
        referer: referrer,
        isExistingCustomer,
        apiUrl: 'https://shop-api.ionos.com/api/v1/saleschannel'
    };
}

/**
 * Sends POST request to SalesChannel API
 * @param {ApiParams} apiParams - API parameters
 * @returns {Promise<SalesChannelResponse>} Promise resolving to API response
 */
export async function callSalesChannelAPI(apiParams) {
    try {
        const response = await fetch(apiParams.apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(apiParams)
        });
        
        if (!response.ok) {
            throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }
        
        return await response.json();
    } catch (error) {
        console.error('SalesChannel API error:', error);
        throw error;
    }
}

// =============================================================================
// ACTION HANDLERS (API Update Flow)
// =============================================================================

/**
 * Builds ITC code from action attributes
 * @param {Object} attributes - Action attributes
 * @returns {string} Encoded ITC code
 */
export function buildItcCode(attributes) {
    const channelcode = attributes.channelcode || '';
    const productcode = attributes.productcode ? `-${attributes.productcode}` : '';
    const campaigncode = attributes.campaigncode ? `-${attributes.campaigncode}` : '';
    
    return encodeURIComponent(channelcode + productcode + campaigncode);
}

/**
 * Handles ADD_TRACKINGCODE_COMP action
 * @param {Object} actionAttributes - Action attributes
 * @returns {string|null} Generated ITC code
 */
export function handleTrackingCodeAction(actionAttributes) {
    const itcCode = buildItcCode(actionAttributes);
    
    if (itcCode) {
        // Set cookie
        setCookie(itcCode);
        debugConsole.log('Tracking code action handled:', itcCode);
        return itcCode;
    }
    
    return null;
}

/**
 * Handles ADD_ACTIONCODE action
 * @param {Object} actionAttributes - Action attributes
 */
export function handleActionCodeAction(actionAttributes) {
    if (actionAttributes.actioncode && isSessionStorageAvailable()) {
        try {
            sessionStorage.setItem('tacosActionCode', actionAttributes.actioncode);
            debugConsole.log('Action code stored:', actionAttributes.actioncode);
        } catch (error) {
            debugConsole.warn('Failed to store action code:', error);
        }
    }
}

/**
 * Handles ADD_MEDIUMCODE action
 * @param {Object} actionAttributes - Action attributes
 */
export function handleMediumCodeAction(actionAttributes) {
    if (actionAttributes.mediumcode && isSessionStorageAvailable()) {
        try {
            sessionStorage.setItem('tacosMediumCode', actionAttributes.mediumcode);
            debugConsole.log('Medium code stored:', actionAttributes.mediumcode);
        } catch (error) {
            debugConsole.warn('Failed to store medium code:', error);
        }
    }
}

/**
 * Handles ADD_COOKIE action
 * @param {Object} actionAttributes - Action attributes
 */
export function handleCookieAction(actionAttributes) {
    if (actionAttributes.cookieName && actionAttributes.cookieValue) {
        setCookie(actionAttributes.cookieValue, actionAttributes.cookieName);
        debugConsole.log('Custom cookie set:', actionAttributes.cookieName, actionAttributes.cookieValue);
    }
}

/**
 * Handles a single TACOS action from API response
 * @param {TacosAction} action - Action to handle
 * @returns {string|null} Generated ITC code (for tracking code actions only)
 */
// eslint-disable-next-line max-lines-per-function
export function handleTacosAction(action) {
    const { action: actionType, actionAttributes } = action;
    
    switch (actionType) {
    case 'ADD_TRACKINGCODE_COMP':
        return handleTrackingCodeAction(actionAttributes);
    case 'ADD_ACTIONCODE':
        handleActionCodeAction(actionAttributes);
        break;
    case 'ADD_MEDIUMCODE':
        handleMediumCodeAction(actionAttributes);
        break;
    case 'ADD_COOKIE':
        handleCookieAction(actionAttributes);
        break;
    default:
        debugConsole.warn('Unknown TACOS action type:', actionType);
    }
    
    return null;
}

// =============================================================================
// MAIN INITIALIZATION FUNCTION
// =============================================================================

/**
 * Main TACOS module initialization function
 * Implements the complete sequence from the PlantUML diagram:
 * 1. Data Collection Phase
 * 2. Session Validation  
 * 3. Data Consistency Check
 * 4. API Update Flow (if needed)
 * 
 * @param {Function} [callback] - Optional callback function
 */
// eslint-disable-next-line max-lines-per-function
export async function initTacosModule(callback = () => {}) {
    debugConsole.log('TACOS Module - Starting initialization');
    
    try {
        // =============================================================================
        // DATA COLLECTION PHASE
        // =============================================================================
        debugConsole.log('Phase 1: Data Collection');
        
        // Get ITC from URL parameter or cookie
        let currentItc = getQueryParam('itc');
        if (!currentItc) {
            currentItc = getCookie('itc');
            debugConsole.log('Using ITC from cookie:', currentItc);
        } else {
            debugConsole.log('Using ITC from URL parameter:', currentItc);
        }
        
        // Get other current values
        const currentReferrer = getDocumentReferrer();
        const currentIsExistingCustomer = getLoginState();
        
        debugConsole.log('Collected data:', {
            itc: currentItc,
            referrer: currentReferrer,
            isExistingCustomer: currentIsExistingCustomer
        });
        
        // =============================================================================
        // SESSION VALIDATION
        // =============================================================================
        debugConsole.log('Phase 2: Session Validation');
        
        const storedData = getStoredTacosData();
        debugConsole.log('Stored data:', storedData);
        
        // =============================================================================
        // DATA CONSISTENCY CHECK
        // =============================================================================
        debugConsole.log('Phase 3: Data Consistency Check');
        
        const parametersMatch = doParametersMatch(
            currentItc,
            currentReferrer,
            currentIsExistingCustomer,
            storedData
        );
        
        if (parametersMatch) {
            debugConsole.log('✓ All parameters match - No action needed');
            callback();
            return;
        }
        
        // =============================================================================
        // API UPDATE FLOW
        // =============================================================================
        debugConsole.log('Phase 4: API Update Flow - Data mismatch detected');
        
        // Build API parameters
        const apiParams = buildApiParams(currentItc, currentReferrer, currentIsExistingCustomer);
        debugConsole.log('Calling SalesChannel API with params:', apiParams);
        
        // Call SalesChannel API
        const apiResponse = await callSalesChannelAPI(apiParams);
        debugConsole.log('API Response received:', apiResponse);
        
        // Process actions from API response
        let updatedItc = currentItc;
        if (apiResponse.actions && Array.isArray(apiResponse.actions)) {
            debugConsole.log(`Processing ${apiResponse.actions.length} actions`);
            
            for (const action of apiResponse.actions) {
                const result = handleTacosAction(action);
                // If this action generated an ITC code, use it for storage
                if (result) {
                    updatedItc = result;
                }
            }
        }
        
        // Update session storage with new values
        setStoredTacosData(updatedItc, currentReferrer, currentIsExistingCustomer);
        debugConsole.log('✓ Session storage updated with new values');
        
        debugConsole.log('✓ TACOS initialization complete');
        callback();
        
    } catch (error) {
        debugConsole.error('TACOS initialization failed:', error);
        // Still call callback to prevent hanging
        callback();
    }
}

// =============================================================================
// UTILITY FUNCTIONS FOR EXTERNAL USE
// =============================================================================

/**
 * Gets all stored TACOS data for debugging/inspection
 * @returns {Object} All stored TACOS data
 */
export function getAllStoredTacosData() {
    const baseData = getStoredTacosData();
    
    if (isSessionStorageAvailable()) {
        try {
            return {
                ...baseData,
                tacosActionCode: sessionStorage.getItem('tacosActionCode'),
                tacosMediumCode: sessionStorage.getItem('tacosMediumCode')
            };
        } catch (error) {
            debugConsole.warn('Failed to read additional session data:', error);
        }
    }
    
    return baseData;
}

/**
 * Clears all TACOS data (for testing/debugging)
 */
export function clearAllTacosData() {
    if (isSessionStorageAvailable()) {
        try {
            const keys = ['tacosItc', 'tacosReferrer', 'tacosIsExistingCustomer', 'tacosActionCode', 'tacosMediumCode'];
            keys.forEach(key => sessionStorage.removeItem(key));
            debugConsole.log('All TACOS session data cleared');
        } catch (error) {
            debugConsole.warn('Failed to clear session data:', error);
        }
    }
}

export default {
    initTacosModule,
    getAllStoredTacosData,
    clearAllTacosData
};
